import { getAppLocale } from 'utils/localStorage';

import LOCALES from 'constants/locales';
import { LOCALE } from 'constants/searchParams';

const SUPPORTED_LANGS = Object.values(LOCALES) as string[];
const DEFAULT_BY_BASE_LANG: Record<string, string> = {
  en: LOCALES.ENGLISH_US,
  es: LOCALES.SPANISH_ES,
  pt: LOCALES.PORTUGUESE_BR,
  fr: LOCALES.FRENCH_FR,
  ru: LOCALES.RUSSIAN_RU,
  de: LOCALES.GERMAN_DE,
  hi: LOCALES.HINDI_IN,
  tr: LOCALES.TURKISH_TR,
  az: LOCALES.AZERBAIJANI_AZ,
  ro: LOCALES.ROMANIAN_RO,
  mo: LOCALES.ROMANIAN_RO, // Moldavian (Romanian)
  uk: LOCALES.UKRAINIAN
};

const LATAM_REGIONS = new Set([
  'AR',
  'BO',
  'BR',
  'CL',
  'CO',
  'CR',
  'CU',
  'DO',
  'EC',
  'GT',
  'HN',
  'MX',
  'NI',
  'PA',
  'PE',
  'PR',
  'PY',
  'SV',
  'UY',
  'VE'
]);

const pickSupportedFromBrowserLocale = (locale: string): string | null => {
  const trimmed = locale?.trim().replace(/_/g, '-');
  if (!trimmed) {
    return null;
  }

  if (SUPPORTED_LANGS.includes(trimmed)) {
    return trimmed;
  }

  const [baseRaw, regionRaw] = trimmed.split('-');
  const base = (baseRaw ?? '').toLowerCase();
  const region = (regionRaw ?? '').toUpperCase();

  if (base === 'es') {
    const preferred =
      region && LATAM_REGIONS.has(region)
        ? LOCALES.SPANISH_LATAM
        : LOCALES.SPANISH_ES;

    if (SUPPORTED_LANGS.includes(preferred)) {
      return preferred;
    }
  }

  if (base === 'pt') {
    const preferred =
      region === 'BR' ? LOCALES.PORTUGUESE_BR : LOCALES.PORTUGUESE_PT;

    if (SUPPORTED_LANGS.includes(preferred)) {
      return preferred;
    }
  }

  const mapped = DEFAULT_BY_BASE_LANG[base];
  if (mapped && SUPPORTED_LANGS.includes(mapped)) {
    return mapped;
  }

  return null;
};

export const detectAppLocale = (): string => {
  const storedLocale = getAppLocale();

  if (storedLocale && SUPPORTED_LANGS.includes(storedLocale)) {
    return storedLocale;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const landingLang = urlParams.get(LOCALE);

  if (landingLang && SUPPORTED_LANGS.includes(landingLang)) {
    return landingLang;
  }

  const browserLocales = navigator.languages?.length
    ? navigator.languages
    : [navigator.language];

  for (const browserLocale of browserLocales) {
    const supported = pickSupportedFromBrowserLocale(browserLocale);
    if (supported) {
      return supported;
    }
  }

  return LOCALES.ENGLISH_US;
};
